import { type FormikProps, useFormik } from 'formik';
import { useSelector } from 'react-redux';
import { object } from 'yup';

import {
  COUNTRY_POSTAL_CODE_REGEX,
  COUNTRY_REGIONS,
} from '@frontend/addresses';
import { AddressInformation, useValidateAddress } from '@frontend/api';
import {
  FormControl,
  FormLabel,
  FormErrorMessage,
  HStack,
  Input,
  VStack,
  useToast,
} from '@frontend/components';

import {
  transformShippingInfoToValues,
  getAddressValidationRules,
  transformValuesToShippingInfo,
} from 'components/Forms/AddressForm';

import {
  RegionSelect,
  CountrySelect,
} from 'modules/common/components/addressFormFields';

import { getDefaultFacilityCountry } from 'store/session/selectors';

import { FormikHooksReactSelectWrapper } from 'utils/FormikHooksReactSelectWrapper';

type OneTimeAddressFormValues = {
  businessName: string;
  attention: string;
  address1: string;
  address2: string;
  city: string;
  state: string;
  postalCode: string;
  country: string;
};

export const useOneTimeAddressForm = ({
  initialAddress,
  openSuggestedAddressModal,
  openUnvalidatedAddressPopover,
  setOneTimeAddress,
}: {
  initialAddress: AddressInformation | null;
  openSuggestedAddressModal: (payload: {
    pendingAddress: AddressInformation;
    suggestedAddresses: AddressInformation[];
  }) => void;
  openUnvalidatedAddressPopover: (payload: {
    pendingAddress: AddressInformation;
  }) => void;
  setOneTimeAddress: (address: AddressInformation) => void;
}) => {
  const defaultCountry = useSelector(getDefaultFacilityCountry);
  const { mutateAsync: validateAddressAsync } = useValidateAddress();
  const toast = useToast();
  const initialValues = transformShippingInfoToValues(
    initialAddress,
    defaultCountry.abbr,
  );
  return useFormik<OneTimeAddressFormValues>({
    initialValues,
    validationSchema: object().shape(getAddressValidationRules()),
    enableReinitialize: true,
    validateOnMount: true,
    onSubmit: async (values) => {
      const address = transformValuesToShippingInfo(values);
      try {
        const { isValid, candidates } = await validateAddressAsync(address);
        if (isValid) {
          if (candidates.length > 0) {
            openSuggestedAddressModal({
              pendingAddress: address,
              suggestedAddresses: candidates,
            });
          } else {
            setOneTimeAddress(address);
          }
        } else {
          openUnvalidatedAddressPopover({ pendingAddress: address });
        }
      } catch (error) {
        console.error('Error validating address:', error);
        toast({
          status: 'error',
          title: "Couldn't validate address",
          description: 'If the problem persists, contact support.',
        });
      }
    },
  });
};

type OneTimeAddressFormProps = {
  formikProps: FormikProps<OneTimeAddressFormValues>;
};

export const OneTimeAddressForm = ({
  formikProps,
}: OneTimeAddressFormProps) => {
  const { regions, postalCodeLabel, regionLabel } =
    COUNTRY_REGIONS[formikProps.values.country] ?? {};
  const postalCodeRegex = COUNTRY_POSTAL_CODE_REGEX[formikProps.values.country];

  return (
    <VStack gap={2} as="form" onSubmit={formikProps.handleSubmit}>
      <FormControl
        isRequired
        isInvalid={
          !!formikProps.errors.businessName && formikProps.touched.businessName
        }
      >
        <FormLabel>Business Name</FormLabel>
        <Input
          name="businessName"
          autoComplete="new-password"
          value={formikProps.values.businessName}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
        />
        <FormErrorMessage>{formikProps.errors.businessName}</FormErrorMessage>
      </FormControl>
      <FormControl
        isRequired
        isInvalid={
          !!formikProps.errors.attention && formikProps.touched.attention
        }
      >
        <FormLabel>Attention</FormLabel>
        <Input
          name="attention"
          autoComplete="new-password"
          value={formikProps.values.attention}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
        />
        <FormErrorMessage>{formikProps.errors.attention}</FormErrorMessage>
      </FormControl>
      <FormControl
        isRequired
        isInvalid={
          !!formikProps.errors.address1 && formikProps.touched.address1
        }
      >
        <FormLabel>Street Address 1</FormLabel>
        <Input
          name="address1"
          autoComplete="new-password"
          value={formikProps.values.address1}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
        />
        <FormErrorMessage>{formikProps.errors.address1}</FormErrorMessage>
      </FormControl>
      <FormControl
        isInvalid={
          !!formikProps.errors.address2 && formikProps.touched.address2
        }
      >
        <FormLabel>Street Address 2</FormLabel>
        <Input
          name="address2"
          autoComplete="new-password"
          value={formikProps.values.address2}
          onChange={formikProps.handleChange}
          onBlur={formikProps.handleBlur}
        />
        <FormErrorMessage>{formikProps.errors.address2}</FormErrorMessage>
      </FormControl>
      <HStack gap={1.5} width="100%" alignItems="flex-start">
        <FormControl
          isRequired
          isInvalid={!!formikProps.errors.city && formikProps.touched.city}
        >
          <FormLabel>City</FormLabel>
          <Input
            name="city"
            autoComplete="new-password"
            value={formikProps.values.city}
            onChange={formikProps.handleChange}
            onBlur={formikProps.handleBlur}
          />
          <FormErrorMessage>{formikProps.errors.city}</FormErrorMessage>
        </FormControl>
        <FormControl
          isRequired={!!regions?.length}
          isDisabled={!regions?.length}
          isInvalid={!!formikProps.errors.state && formikProps.touched.state}
        >
          <FormLabel>{regionLabel ?? 'State/Province'}</FormLabel>
          <FormikHooksReactSelectWrapper
            name="state"
            setFieldTouched={formikProps.setFieldTouched}
            setFieldValue={formikProps.setFieldValue}
            values={formikProps.values}
          >
            <RegionSelect
              autoComplete="new-password"
              country={formikProps.values.country}
            />
          </FormikHooksReactSelectWrapper>
          <FormErrorMessage>{formikProps.errors.state}</FormErrorMessage>
        </FormControl>
      </HStack>
      <HStack gap={1.5} width="100%" alignItems="flex-start">
        <FormControl
          isInvalid={
            !!formikProps.errors.postalCode && formikProps.touched.postalCode
          }
          isRequired={!!postalCodeRegex}
        >
          <FormLabel>{postalCodeLabel ?? 'Postal Code'}</FormLabel>
          <Input
            name="postalCode"
            autoComplete="new-password"
            value={formikProps.values.postalCode}
            onChange={formikProps.handleChange}
            onBlur={formikProps.handleBlur}
          />
          <FormErrorMessage>{formikProps.errors.postalCode}</FormErrorMessage>
        </FormControl>
        <FormControl
          isInvalid={
            !!formikProps.errors.country && formikProps.touched.country
          }
        >
          <FormLabel>Country</FormLabel>
          <FormikHooksReactSelectWrapper
            name="country"
            setFieldTouched={formikProps.setFieldTouched}
            setFieldValue={formikProps.setFieldValue}
            values={formikProps.values}
          >
            <CountrySelect
              autoComplete="new-password"
              onChange={() => {
                formikProps.setFieldValue('state', '');
                formikProps.setFieldValue('postalCode', '');
                formikProps.setFieldTouched('state', false);
                formikProps.setFieldTouched('postalCode', false);
              }}
            />
          </FormikHooksReactSelectWrapper>
          <FormErrorMessage>{formikProps.errors.country}</FormErrorMessage>
        </FormControl>
      </HStack>
    </VStack>
  );
};
